/**
 * PWA management hook for CaribbeanLeaf.
 *
 * Handles service worker registration, update prompts, install prompts,
 * and engagement tracking for install criteria.
 */

import { useRegisterSW } from 'virtual:pwa-register/react';
import { useCallback, useEffect, useState } from 'react';

interface PWAState {
  needRefresh: boolean;
  offlineReady: boolean;
  canInstall: boolean;
}

export interface UsePWAReturn extends PWAState {
  updateServiceWorker: () => void;
  installApp: () => Promise<void>;
  dismissInstall: () => void;
}

const INSTALL_CRITERIA = {
  minVisits: 2,
  minEngagement: 30, // seconds
  minPages: 3,
};

export function usePWA(): UsePWAReturn {
  const [canInstall, setCanInstall] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  const {
    needRefresh: [needRefresh],
    offlineReady: [offlineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        // Check for updates every 60 minutes
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000);
      }
    },
    onRegisterError(error) {
      console.error('SW registration error:', error);
    },
  });

  // Listen for beforeinstallprompt and track engagement
  useEffect(() => {
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);

      // Check if user meets install criteria
      const visits = parseInt(localStorage.getItem('cl_visits') || '0', 10);
      const engagement = parseInt(localStorage.getItem('cl_engagement') || '0', 10);
      const pages = parseInt(localStorage.getItem('cl_pages') || '0', 10);
      const dismissed = localStorage.getItem('cl_install_dismissed');

      if (
        !dismissed &&
        visits >= INSTALL_CRITERIA.minVisits &&
        engagement >= INSTALL_CRITERIA.minEngagement &&
        pages >= INSTALL_CRITERIA.minPages
      ) {
        setCanInstall(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Track visit count
    const visits = parseInt(localStorage.getItem('cl_visits') || '0', 10);
    localStorage.setItem('cl_visits', String(visits + 1));

    // Track engagement time on unload
    const startTime = Date.now();
    const trackEngagement = () => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const total = parseInt(localStorage.getItem('cl_engagement') || '0', 10);
      localStorage.setItem('cl_engagement', String(total + elapsed));
    };

    window.addEventListener('beforeunload', trackEngagement);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('beforeunload', trackEngagement);
    };
  }, []);

  // Track page views
  useEffect(() => {
    const pages = parseInt(localStorage.getItem('cl_pages') || '0', 10);
    localStorage.setItem('cl_pages', String(pages + 1));
  }, []);

  const installApp = useCallback(async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    await deferredPrompt.userChoice;

    setDeferredPrompt(null);
    setCanInstall(false);
  }, [deferredPrompt]);

  const dismissInstall = useCallback(() => {
    setCanInstall(false);
    localStorage.setItem('cl_install_dismissed', 'true');
  }, []);

  return {
    needRefresh,
    offlineReady,
    canInstall,
    updateServiceWorker: () => updateServiceWorker(true),
    installApp,
    dismissInstall,
  };
}
